import { MAPBOX_FORWARD_GEOCODE_URL } from "./src/map/constants.js";

export const LOCATION_AUTOCOMPLETE_DEBOUNCE_MS = 300;
export const LOCATION_AUTOCOMPLETE_REQUEST_TIMEOUT_MS = 5000;
export const MIN_LOCATION_QUERY_LENGTH = 2;

export function emptyLocationAutocompleteState(query = "") {
  return {
    status: "idle",
    query,
    suggestions: [],
  };
}

export function recentTravelLocationIsComplete(exposures, autocomplete = emptyLocationAutocompleteState()) {
  const locationText = exposures.recentTravelLocation.trim();
  if (!locationText) return false;
  if (exposures.recentTravelLocationSelection?.label === locationText) return true;
  if (exposures.recentTravelLocationFallbackAllowed) return true;
  if (["empty", "error"].includes(autocomplete.status)) return true;
  return autocomplete.status === "idle" && autocomplete.query === locationText && locationText.length >= MIN_LOCATION_QUERY_LENGTH;
}

export function buildMapboxForwardGeocodeUrl(query, token, endpoint = MAPBOX_FORWARD_GEOCODE_URL) {
  const params = new URLSearchParams({
    q: query,
    types: "place,region,country",
    limit: "5",
    access_token: token,
  });
  return `${endpoint}?${params.toString()}`;
}

export function formatMapboxLocationFeature(feature) {
  const properties = feature?.properties || {};
  const context = properties.context || {};
  const type = properties.feature_type || feature?.place_type?.[0] || "";
  const parts = [
    properties.name,
    context.place?.name,
    context.region?.name,
    context.country?.name,
  ];
  const placeFormattedParts = String(properties.place_formatted || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const legacyPlaceParts = String(feature?.place_name || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const label = uniqueLocationParts(
    parts.filter(Boolean).length > 1
      ? parts
      : [properties.name, ...placeFormattedParts, ...legacyPlaceParts]
  ).join(", ");
  if (!label) return null;
  return {
    id: properties.mapbox_id || feature?.id || label,
    label,
    type,
  };
}

export async function searchMapboxLocations(query, {
  fetchImpl = globalThis.fetch,
  token = "",
  endpoint = MAPBOX_FORWARD_GEOCODE_URL,
  timeoutMs = LOCATION_AUTOCOMPLETE_REQUEST_TIMEOUT_MS,
  setTimeoutFn = globalThis.setTimeout,
  clearTimeoutFn = globalThis.clearTimeout,
} = {}) {
  const normalizedQuery = String(query || "").trim();
  if (normalizedQuery.length < MIN_LOCATION_QUERY_LENGTH || !token) return emptyLocationAutocompleteState(normalizedQuery);
  const response = await withTimeout(
    fetchImpl(buildMapboxForwardGeocodeUrl(normalizedQuery, token, endpoint)),
    timeoutMs,
    setTimeoutFn,
    clearTimeoutFn
  );
  if (!response.ok) throw new Error("location_lookup_unavailable");
  const body = await response.json();
  const suggestions = uniqueSuggestions((body.features || []).map(formatMapboxLocationFeature).filter(Boolean));
  return {
    status: suggestions.length ? "ready" : "empty",
    query: normalizedQuery,
    suggestions,
  };
}

export function createLocationAutocompleteSearch({
  fetchImpl = globalThis.fetch,
  tokenProvider = () => "",
  endpoint = MAPBOX_FORWARD_GEOCODE_URL,
  delay = LOCATION_AUTOCOMPLETE_DEBOUNCE_MS,
  requestTimeoutMs = LOCATION_AUTOCOMPLETE_REQUEST_TIMEOUT_MS,
  setTimeoutFn = globalThis.setTimeout,
  clearTimeoutFn = globalThis.clearTimeout,
} = {}) {
  let sequence = 0;
  let timer = null;
  let resolvePending = null;

  return function search(query) {
    const normalizedQuery = String(query || "").trim();
    sequence += 1;
    const requestSequence = sequence;
    if (timer) clearTimeoutFn(timer);
    if (resolvePending) resolvePending({ ...emptyLocationAutocompleteState(normalizedQuery), status: "stale" });
    if (normalizedQuery.length < MIN_LOCATION_QUERY_LENGTH) return Promise.resolve(emptyLocationAutocompleteState(normalizedQuery));

    return new Promise((resolve) => {
      resolvePending = resolve;
      timer = setTimeoutFn(async () => {
        timer = null;
        resolvePending = null;
        try {
          const result = await searchMapboxLocations(normalizedQuery, {
            fetchImpl,
            token: tokenProvider(),
            endpoint,
            timeoutMs: requestTimeoutMs,
          });
          resolve(requestSequence === sequence ? result : { ...emptyLocationAutocompleteState(normalizedQuery), status: "stale" });
        } catch {
          resolve(requestSequence === sequence ? { ...emptyLocationAutocompleteState(normalizedQuery), status: "error" } : { ...emptyLocationAutocompleteState(normalizedQuery), status: "stale" });
        }
      }, delay);
    });
  };
}

function withTimeout(promise, timeoutMs, setTimeoutFn, clearTimeoutFn) {
  return new Promise((resolve, reject) => {
    const timer = setTimeoutFn(() => reject(new Error("location_lookup_timeout")), timeoutMs);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeoutFn(timer);
        resolve(value);
      },
      (error) => {
        clearTimeoutFn(timer);
        reject(error);
      }
    );
  });
}

function uniqueLocationParts(parts) {
  const seen = new Set();
  return parts
    .map((part) => String(part || "").trim())
    .filter((part) => {
      const key = part.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function uniqueSuggestions(suggestions) {
  const seen = new Set();
  return suggestions.filter((suggestion) => {
    const key = suggestion.label.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
